// EditUserForm.js 
import { useState, useEffect, useRef } from "react"
import { useUpdateUserMutation, useDeleteUserMutation, useGetUserActivityLogsQuery } from "./usersApiSlice"
import { useNavigate } from "react-router-dom"
import { ROLES } from "../../config/roles"
import useTitle from '../../hooks/useTitle'
import { Modal, Spinner } from 'react-bootstrap'

const USER_REGEX = /^[A-Za-z0-9._]{3,20}$/
const PWD_REGEX = /^[A-Za-z0-9!@#$%]{4,12}$/

const EditUserForm = ({ user }) => {
    useTitle(`Nhà đẹp - Chỉnh sửa: ${user.username}`)

    const [updateUser, { isLoading, isSuccess, isError, error }] = useUpdateUserMutation()
    const [deleteUser, { isSuccess: isDelSuccess }] = useDeleteUserMutation()
    const navigate = useNavigate()

    const [username, setUsername] = useState(user.username)
    const [validUsername, setValidUsername] = useState(true)
    const [password, setPassword] = useState('')
    const [validPassword, setValidPassword] = useState(true)
    const [role, setRole] = useState(user.roles[0] || "Sale") // Chỉ lấy vai trò đầu tiên
    const [active, setActive] = useState(user.active)
    const [phoneViewQuota, setPhoneViewQuota] = useState(user.phoneViewQuota)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [showLogModal, setShowLogModal] = useState(false)
    const [logPage, setLogPage] = useState(1)

    const passwordRef = useRef()
    const isAdmin = user.roles.includes('Admin')
    const {
        data: logData,
        isLoading: logLoading,
        isFetching: logFetching,
        refetch
    } = useGetUserActivityLogsQuery(
        { userId: user.id, page: logPage },
        { 
            skip: !showLogModal,
            refetchOnMountOrArgChange: true 
        }
    )
    useEffect(() => {
        setValidUsername(USER_REGEX.test(username))
    }, [username])

    useEffect(() => {
        if (password) {
            setValidPassword(PWD_REGEX.test(password))
        } else {
            setValidPassword(true) // Nếu để trống thì hợp lệ
        }
    }, [password])

    useEffect(() => {
        if (isSuccess || isDelSuccess) {
            navigate('/dash/users')
        }
    }, [isSuccess, isDelSuccess, navigate])

    // Chống autofill mật khẩu
    useEffect(() => {
        if (passwordRef.current) {
            passwordRef.current.setAttribute('autocomplete', 'new-password')
        }
    }, [])

    const canSave = validUsername && (password ? validPassword : true) && !isLoading

    const onSave = async () => {
        const updateData = {
            id: user.id,
            username,
            roles: [role],
            active,
            phoneViewQuota: Number(phoneViewQuota)
        }
        if (password) updateData.password = password
        await updateUser(updateData)
    }

    const onDelete = async () => {
        await deleteUser({ id: user.id })
    }

    const roleOptions = Object.values(ROLES).map(r => (
        <option key={r} value={r}>{r}</option>
    ))

    const getActionBadgeClass = (action) => {
        if (action.includes('VIEW_PHONE')) return 'bg-warning text-white'
        if (action.includes('CREATE')) return 'bg-info text-white'
        if (action.includes('UPDATE')) return 'bg-warning text-white'
        if (action.includes('DELETE')) return 'bg-danger text-white'
        if (action.includes('RECOVER')) return 'bg-success text-white'
        if (action === 'LOGIN') return 'bg-success text-white'
        if (action.includes('LOGIN_FAILED')) return 'bg-danger text-white'
        if (action.includes('LOGOUT')) return 'bg-danger text-white'
        return 'bg-secondary text-white'
    }

    return (
        <>
            <div className="container-fluid py-4">
                <div className="row justify-content-center">
                    <div className="col-12 col-lg-8 col-xl-6">
                        <div className="card shadow border-0">
                            <div className="card-header bg-warning text-dark text-center">
                                <h4 className="mb-0">
                                    <i className="bi bi-person-gear me-2"></i>Chỉnh sửa người dùng
                                </h4>
                            </div>

                            <div className="card-body p-4">

                                {/* Thông báo lỗi */}
                                {isError && (
                                    <div className="alert alert-danger alert-dismissible fade show">
                                        <strong>Lỗi:</strong> {error?.data?.message || "Không thể cập nhật"}
                                        <button type="button" className="btn-close" data-bs-dismiss="alert"></button>
                                    </div>
                                )}

                                <form onSubmit={(e) => e.preventDefault()}>
                                    {/* Username */}
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">
                                            Tên đăng nhập <span className="text-danger">(3-20 chữ cái hoặc số)</span>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text disable"></span>
                                            <input
                                                type="text"
                                                className={`form-control ${username && !validUsername ? 'is-invalid' : ''}`}
                                                value={username}
                                                onChange={(e) => setUsername(e.target.value)}
                                                autoComplete="off"
                                                placeholder="Ví dụ: admin2025, sale.hcm"
                                            />
                                        </div>
                                        {username && !validUsername && (
                                            <div className="text-danger small mt-1">
                                                Chỉ dùng a-z, 0-9, dấu chấm, gạch dưới. Độ dài 3-20
                                            </div>
                                        )}
                                    </div>

                                    {/* Password */}
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">
                                            Mật khẩu <span className="text-danger">(4-12 ký tự, cho phép !@#$%)</span>{' '}<span className="text-secondary">(để trống = không đổi)</span>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text"></span>
                                            <input
                                                type="password"
                                                className={`form-control ${password && !validPassword ? 'is-invalid' : ''}`}
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                ref={passwordRef}
                                                autoComplete="new-password"
                                                placeholder="Nhập mật khẩu mới nếu muốn đổi"
                                            />
                                        </div>
                                        {password && !validPassword && (
                                            <div className="text-danger small mt-1">
                                                Mật khẩu phải 4-12 ký tự, chỉ dùng chữ, số và !@#$%
                                            </div>
                                        )}
                                    </div>

                                    {/* Vai trò - Dropdown đơn */}
                                    <div className="mb-3">
                                        <label className="form-label fw-bold">Vai trò</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-white border-end-0">
                                                <i className="fas fa-user-tag text-primary"></i>
                                            </span>
                                            <select
                                                className="form-select border-start-0 ps-0"
                                                value={role}
                                                onChange={(e) => setRole(e.target.value)}
                                            >
                                                {roleOptions}
                                            </select>
                                        </div>
                                    </div>

                                    {role !== 'Admin' && (
                                        <div className="mb-3">
                                            <label className="form-label fw-bold">
                                                Lượt xem SĐT còn lại
                                            </label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={phoneViewQuota}
                                                onChange={(e) => setPhoneViewQuota(e.target.value)}
                                                min="0"
                                                max="1000"
                                            />
                                            <small className="text-muted">Mặc định: 20. Sẽ tự reset lúc 0h hàng ngày.</small>
                                        </div>
                                    )}

                                    {/* Trạng thái */}
                                    <div className="mb-4">
                                        <label className="form-label fw-bold">Trạng thái</label>
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="user-active"
                                                checked={active}
                                                onChange={() => setActive(prev => !prev)}
                                            />
                                            <label className="form-check-label fw-medium" htmlFor="user-active">
                                                {active ? 'Đang hoạt động' : 'Bị vô hiệu hóa'}
                                            </label>
                                        </div>
                                    </div>

                                    {/* Nút hành động */}
                                    <div className="d-flex justify-content-end gap-3 mt-4">
                                        <button
                                            type="button"
                                            className="btn btn-outline-info px-2"
                                            onClick={() => {
                                                setShowLogModal(true)
                                                setLogPage(1)
                                                refetch()
                                            }}
                                        >
                                            Nhật ký
                                        </button>
                                        
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary px-2"
                                            onClick={() => navigate('/dash/users')}
                                        >
                                            Hủy bỏ
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-danger px-2"
                                            onClick={() => setShowDeleteModal(true)}
                                        >
                                            Xóa tài khoản
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-primary px-2"
                                            onClick={onSave}
                                            disabled={!canSave || isLoading}
                                        >
                                            {isLoading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                                    Đang lưu...
                                                </>
                                            ) : (
                                                <>Lưu thay đổi</>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal xác nhận xóa - CHÍNH GIỮA MÀN HÌNH */}
            {showDeleteModal && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.6)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg">
                            <div className="modal-body py-4 text-center">
                                <i className="bi bi-person-dash text-danger fa-3x mb-3"></i>
                                <h3 className="modal-title text-danger text-center fw-bold">
                                    Xác nhận xóa tài khoản
                                </h3>
                                <br/>
                                <p className="mb-2">Bạn có chắc chắn muốn <strong>xóa vĩnh viễn</strong> tài khoản: {' '}
                                    <span className="text-danger fw-bold">{user.username}</span>
                                </p>
                                <small className="text-muted">Hành động này không thể hoàn tác!</small>
                            </div>
                            <div className="modal-footer border-0 justify-content-center gap-3">
                                <button
                                    type="button"
                                    className="btn btn-secondary px-4"
                                    onClick={() => setShowDeleteModal(false)}
                                >
                                    Hủy bỏ
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger px-5"
                                    onClick={onDelete}
                                >
                                    Xóa vĩnh viễn
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ==================== MODAL NHẬT KÝ HOẠT ĐỘNG ==================== */}
            <Modal show={showLogModal} onHide={() => setShowLogModal(false)} size="lg" centered>
                <Modal.Header closeButton className="bg-dark text-info" closeVariant="white">
                    <Modal.Title>
                        <i className="bi bi-person-vcard me-2"></i>
                        Nhật ký hoạt động - {user.username}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-light text-white" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {logLoading || logFetching ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="warning" />
                            <p className="mt-3">Đang tải nhật ký...</p>
                        </div>
                    ) : logData?.logs?.length ? (
                        <div className="table-responsive">
                            <table className="table table-light table-hover align-middle">
                                <thead className="table-secondary text-dark">
                                    <tr>
                                        <th>Thời gian</th>
                                        <th>Hành động</th>
                                        <th>Chi tiết</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logData.logs.map(log => (
                                        <tr key={log._id}>
                                            <td className="small">
                                                {new Date(log.createdAt).toLocaleString('vi-VN')}
                                            </td>
                                            <td>
                                                <span className={`badge ${getActionBadgeClass(log.action)}`}>
                                                    {log.action === 'VIEW_PHONE' && 'Xem SĐT'}
                                                    {log.action === 'CREATE_PROPERTY' && 'Tạo BĐS'}
                                                    {log.action === 'UPDATE_PROPERTY' && 'Sửa BĐS'}
                                                    {log.action === 'DELETE_PROPERTY' && 'Xóa BĐS'}
                                                    {log.action === 'TEMP_DELETE_PROPERTY' && 'Xóa tạm BĐS'}
                                                    {log.action === 'RECOVER_PROPERTY' && 'Khôi phục BĐS'}
                                                    {log.action === 'LOGIN' && 'Đăng nhập'}
                                                    {log.action === 'LOGIN_FAILED' && 'Đăng nhập thất bại'}
                                                    {log.action === 'LOGOUT' && 'Đăng xuất'}
                                                </span>
                                            </td>
                                            <td className="small">
                                                {log.details?.phone ? `SĐT: ${log.details.phone}` : ''}
                                                {log.details?.address ? ` Địa chỉ: ${log.details.address}` : '-'}
                                                {log.details?.changes?.map((change, i) => (
                                                    <div key={i} className="">
                                                        • {change}
                                                    </div>
                                                ))}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-center text-dark py-5">Chưa có hoạt động nào</p>
                    )}

                    {/* Phân trang */}
                    {logData?.pagination&& (
                        <>
                            <div className="d-flex justify-content-center align-items-center gap-2 mt-4 flex-wrap px-3">
                                <button
                                    className="btn btn-sm btn-outline-dark"
                                    onClick={() => setLogPage(1)}
                                    disabled={logPage === 1}
                                >
                                    ‹‹ Đầu
                                </button>

                                <button
                                    className="btn btn-sm btn-outline-dark"
                                    onClick={() => setLogPage(p => Math.max(1, p - 1))}
                                    disabled={logPage === 1}
                                >
                                    ‹ Trước
                                </button>

                                {/* Số trang thông minh */}
                                {(() => {
                                    const total = logData.pagination.pages
                                    const current = logPage
                                    const pages = []

                                    if (total <= 7) {
                                        for (let i = 1; i <= total; i++) pages.push(i)
                                    } else if (current <= 4) {
                                        pages.push(1, 2, 3, 4, 5, '...', total)
                                    } else if (current >= total - 3) {
                                        pages.push(1, '...', total - 4, total - 3, total - 2, total - 1, total)
                                    } else {
                                        pages.push(1, '...', current - 1, current, current + 1, '...', total)
                                    }

                                    return pages.map((p, idx) =>
                                        p === '...' ? (
                                            <span key={idx} className="px-3 text-dark fw-bold">...</span>
                                        ) : (
                                            <button
                                                key={p}
                                                className={`btn btn-sm ${current === p ? 'btn-dark text-white' : 'btn-outline-dark'}`}
                                                onClick={() => setLogPage(p)}
                                            >
                                                {p}
                                            </button>
                                        )
                                    )
                                })()}

                                <button
                                    className="btn btn-sm btn-outline-dark"
                                    onClick={() => setLogPage(p => p + 1)}
                                    disabled={logPage >= logData.pagination.pages}
                                >
                                    Sau ›
                                </button>

                                <button
                                    className="btn btn-sm btn-outline-dark"
                                    onClick={() => setLogPage(logData.pagination.pages)}
                                    disabled={logPage === logData.pagination.pages}
                                >
                                    Cuối ››
                                </button>
                            </div>

                            <div className="text-center mt-4">
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault()
                                        const input = e.target.elements.goToPage.value.trim()
                                        const target = parseInt(input, 10)
                                        if (target >= 1 && target <= logData.pagination.pages) {
                                            setLogPage(target)
                                        } else {
                                        }
                                        e.target.elements.goToPage.value = ''
                                    }}
                                    className="d-inline-flex align-items-center gap-2"
                                >
                                    <small className="text-dark nowrap">Đi đến trang:</small>
                                    <input
                                        type="number"
                                        name="goToPage"
                                        min="1"
                                        max={logData.pagination.pages}
                                        placeholder={logPage}
                                        className="form-control form-control-sm"
                                        style={{ width: '80px' }}
                                        required
                                    />
                                    <button type="submit" className="btn btn-sm btn-primary">
                                        Đi
                                    </button>
                                </form>
                            </div>

                            {/* Thông tin tổng */}
                            <div className="text-center mt-3">
                                <small className="text-muted">
                                    Trang <strong>{logPage}</strong> / <strong>{logData.pagination.pages}</strong>
                                    {' '} (Tổng <strong>{logData.pagination.total}</strong> hoạt động)
                                </small>
                            </div>
                        </>
                    )}
                </Modal.Body>
            </Modal>
        </>
    )
}

export default EditUserForm